import { useEffect, useMemo, useState } from 'react'
import { Stack } from '@mantine/core'
import KPI_Section from 'app/views/components/KPI/KPI-horizontal-section'
import { getLockedColumnDefs } from './locked-deal-table-columnDefs'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { DealParams } from 'app/models/deal-params'
import { dealScreenRowsAsParams } from 'app/models/deal-screen-row'
import {
  getDealScreenLoader,
  getDealScreenRows,
  isDealScreenStale,
} from 'app/state/ducks/deal-screen/selectors'
import { loadDealScreen } from 'app/state/ducks/deal-screen/thunks'
import { getKPIData } from '../deals-kpi-data'
import MainTable from 'app/views/components/Table/MainTable'
import {
  GridSortingStorage,
  GridFilterStorage,
  GridPaginationStorage,
  GridColumnVisibilityStorage,
  useGridSorting,
  useGridFilters,
  useGridPagination,
  useGridColumnVisibility,
} from 'app/utils/grid-sorting-storage'

/**
 * The Terminated deals grid. Its rows are the terminated rows of the Deals
 * read (`GET /api/deal-management/screen`, ADR_LOS-030): one request, shared
 * with the live Deals grid, with the borrower and admin names and the
 * termination time already on each row. It used to load the locked deals and
 * the whole entity list to name them.
 */
export default function LockedDealManagmentPage() {
  const rows = useSelector(getDealScreenRows)
  const screenLoading = useSelector(getDealScreenLoader)
  const stale = useSelector(isDealScreenStale)
  const deals = useMemo(
    () => dealScreenRowsAsParams(rows.filter(row => row.status === 'Terminated')),
    [rows],
  )
  const [filteredDeals, setFilteredDeals] = useState<DealParams[]>(deals)
  const columnDefs = useMemo(() => getLockedColumnDefs(), [])
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const kpiData = getKPIData(deals, setFilteredDeals)

  // Grid preferences hooks
  const { sorting, handleSortingChange } = useGridSorting(
    GridSortingStorage.lockedDeals,
  )
  const { toMRTColumnFilters, handleMRTFiltersChange } = useGridFilters(
    GridFilterStorage.lockedDeals,
  )
  const { pagination, handlePaginationChange } = useGridPagination(
    GridPaginationStorage.lockedDeals,
  )
  const { columnVisibility, handleColumnVisibilityChange } =
    useGridColumnVisibility(GridColumnVisibilityStorage.lockedDeals)

  useEffect(() => {
    dispatch(loadDealScreen())
  }, [])

  // A write the rows could not be patched for: reload once, while on screen.
  useEffect(() => {
    if (stale && !screenLoading) dispatch(loadDealScreen())
  }, [stale, screenLoading])

  useEffect(() => {
    setFilteredDeals(deals)
  }, [deals])

  return (
    <Stack>
      <KPI_Section
        kpiData={kpiData}
        isLoading={screenLoading}
      />
      <MainTable
        tableName="Locked_Deals Table"
        isLoading={screenLoading}
        withPagination={true}
        csvExportEnabled={true}
        enableTopToolbar={false}
        headerBackgroundColor="#F0EEEE"
        columnDefs={columnDefs}
        data={filteredDeals}
        initialSorting={[
          { id: sorting.sortBy, desc: sorting.order === 'desc' },
        ]}
        onSortingChange={handleSortingChange}
        initialColumnFilters={toMRTColumnFilters()}
        onColumnFiltersChange={handleMRTFiltersChange}
        initialPagination={pagination}
        onPaginationChange={handlePaginationChange}
        initialColumnVisibility={columnVisibility}
        onColumnVisibilityChange={handleColumnVisibilityChange}
        setRow={(e: any, cellName: string) => {
          if (cellName != 'id') {
            navigate('/dealmanagement/deal/' + e.id + '/' + e.status)
          }
        }}
      />
    </Stack>
  )
}
